import { create } from "zustand";
import { Scenario, getDefaultScenario, scenarios } from "../services/scenarios";
import { getOrCreateSession, finishSession, clearSession, updateSessionActivity } from "../utils/session";
interface ScenarioSessionState {
  // Session & Scenario
  sessionId: string;
  currentScenario: Scenario;

  // UI State
  isListening: boolean;
  isProcessing: boolean;
  isSpeaking: boolean;
  error: string | null;
  isInitialized: boolean;
  hasRestoredTranscript: boolean; // kept for compatibility with existing components
  isConversationFinished: boolean;

  // Actions
  setScenario: (scenario: Scenario | string) => void;
  setSessionId: (sessionId: string) => void;
  resetSession: () => void;
  setError: (error: string | null) => void;
  setIsListening: (value: boolean) => void;
  setIsProcessing: (value: boolean) => void;
  setIsSpeaking: (value: boolean) => void;
  setIsInitialized: (value: boolean) => void;
  setHasRestoredTranscript: (value: boolean) => void;
  setIsConversationFinished: (value: boolean) => void;
}

export const useScenarioSessionStore = create<ScenarioSessionState>(
  (set, _) => ({
    // Initial State - use persistent session
    sessionId: getOrCreateSession(),
    currentScenario: getDefaultScenario(),
    isListening: false,
    isProcessing: false,
    isSpeaking: false,
    error: null,
    isInitialized: false,
    hasRestoredTranscript: false,
    isConversationFinished: false,

    // Scenario Management
    setScenario: (scenarioOrId: Scenario | string) => {
      const scenario =
        typeof scenarioOrId === "string"
          ? scenarios[scenarioOrId]
          : scenarioOrId;

      if (scenario) {
        set({ currentScenario: scenario });
        updateSessionActivity();
      }
    },

    setSessionId: (sessionId: string) => {
      set({ sessionId });
    },

    resetSession: () => {
      clearSession();
      set({
        sessionId: getOrCreateSession(),
        currentScenario: getDefaultScenario(),
        isInitialized: false,
        hasRestoredTranscript: false,
        isConversationFinished: false,
      });
    },

    setError: (error: string | null) => {
      set({ error });
    },

    setIsListening: (value: boolean) => {
      set({ isListening: value });
      if (value) updateSessionActivity();
    },
    setIsProcessing: (value: boolean) => {
      set({ isProcessing: value });
      if (value) updateSessionActivity();
    },
    setIsSpeaking: (value: boolean) => set({ isSpeaking: value }),
    setIsInitialized: (value: boolean) => set({ isInitialized: value }),
    setHasRestoredTranscript: (value: boolean) => set({ hasRestoredTranscript: value }),
    setIsConversationFinished: (value: boolean) => {
      set({ isConversationFinished: value });
      if (value) {
        finishSession(); // Mark session as finished when conversation ends
      }
    },
  })
);
